import { useAppDispatch } from '@/app/hooks'
import { Button } from '@/components/atoms/button'
import { Input } from '@/components/atoms/input'
import { NavigateHeader } from '@/components/atoms/navigate-header'
import { Loading } from '@/components/organisms/loading'
import { FormLayout } from '@/components/template/form-layout'
import { Layout } from '@/components/template/layout'
import { changePassword } from '@/features/auth/auth-slice'
import { useQuerySearchParams } from '@/hooks/use-query-search-params'
import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useNavigate, useParams } from 'react-router-dom'
import { passwordRefine } from '@/utils/zod-validate-map'
import { pcScreenStyle, horizontalSpacing, spacerTopStyle } from '@/styles/common'
import { errorHandler } from '@/libs/errors'
import { PasswordResetCompleteModal } from '@/components/organisms/modal/password-reset-complete-modal'

interface SubmitData {
  password: string
  passwordConfirm: string
}

export const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const searchParams = useQuerySearchParams<{ hotelId: string; lang: string; email: string }>()
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [isCompleted, setCompleted] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm<SubmitData>({
    mode: 'all',
    resolver: zodResolver(
      z
        .object({
          password: passwordRefine,
          passwordConfirm: passwordRefine,
        })
        .superRefine((value, ctx) => {
          if (!value.password || !value.passwordConfirm) {
            return
          }
          if (value.password !== value.passwordConfirm) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t('Passwords do not match'),
              path: ['passwordConfirm'],
            })
          }
        }),
    ),
  })

  const onSubmit: SubmitHandler<SubmitData> = async submitData => {
    try {
      setLoading(true)
      await dispatch(
        changePassword({
          token: token!,
          email: searchParams.email,
          password: submitData.password,
        }),
      ).unwrap()
      setCompleted(true)
    } catch (error) {
      errorHandler({ error })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!token) {
      navigate('/error-404')
    }
    i18n.changeLanguage(searchParams.lang)
  }, [])

  return (
    <Layout>
      <NavigateHeader title={t('Reset your password')} style={{ marginBottom: '1rem' }} />
      <div css={pcScreenStyle}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormLayout>
            <Input
              required
              {...register('password')}
              type="password"
              label={t('New Password')}
              placeholder={t('Must include lowercase, uppercase letters and numbers')}
              error={errors.password?.message}
              autoComplete="new-password"
            />

            <div css={spacerTopStyle}>
              <Input
                required
                {...register('passwordConfirm')}
                type="password"
                label={t('Re-enter new password')}
                placeholder={t('Please fill in this field')}
                error={errors.passwordConfirm?.message}
                autoComplete="new-password"
              />
            </div>
          </FormLayout>
          <Loading loading={loading}>
            <Button disabled={!isDirty || !isValid} buttonCss={[spacerTopStyle, horizontalSpacing]} type="submit" text={t('Resetting')} />
          </Loading>
        </form>
      </div>
      <PasswordResetCompleteModal isOpen={isCompleted} />
    </Layout>
  )
}
